import React from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { Box, Button, DataListItem, Box as Form, Input } from '@node-space/ui-components'
import { RequestErrorCallout } from 'components/Errors/RequestErrorCallout'
import { contactInstrumentAtom } from 'pages/Contacts/atoms/contactAtoms'
import { useWalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { sendWalletAtom } from './atoms/atoms'
import { SendDetailsCard } from './components/SendDetailsCard'
import { useSendDetailsForm } from './hooks/useSendDetailsForm'

export const SendDetails = () => {
  const { t } = useTranslation()
  const sendWallet = useAtomValue(sendWalletAtom)
  const contactInstrument = useAtomValue(contactInstrumentAtom)
  const [walletAction] = useWalletActionContext()

  const {
    formFields,
    estimate,
    isDisabledReviewPayment,
    isLoadingReviewPayment,
    requestError,
    showTimer,
    timeLeft,
    sameCurrency,
    handleSubmit,
    onSubmit,
    setChangedField,
    setValue,
    setSource,
  } = useSendDetailsForm()

  const exchangeRate = estimate?.pegged ? '1' : estimate?.exchangeRate

  return (
    <Form
      tag="form"
      flexCol
      gapY={20}
      data-testid="send-details-form"
      onSubmit={handleSubmit(onSubmit)}
    >
      <SendDetailsCard />

      {requestError?.show && <RequestErrorCallout requestError={requestError} />}
      <Box flex gapX={12} width="full" justify="between">
        <Box width="full">
          <Input
            testid="send-amount"
            {...formFields.sendAmount}
            disabled={isLoadingReviewPayment}
            onChange={e => {
              setValue('sendAmount', e?.target?.value)
              setChangedField('sendAmount')
              setSource('user')
            }}
            rightSuffixText={sendWallet?.currency?.code}
          />
        </Box>
        {!sameCurrency && (
          <Box width="full">
            <Input
              testid="receive-amount"
              {...formFields.receiveAmount}
              disabled={isLoadingReviewPayment}
              onChange={e => {
                setValue('receiveAmount', e?.target?.value)
                setChangedField('receiveAmount')
                setSource('user')
              }}
              rightSuffixText={contactInstrument?.currency}
            />
          </Box>
        )}
      </Box>
      <Box width="full" data-testid="details-list">
        <Box flexCol width="full">
          {!sameCurrency && (
            <DataListItem
              id="exchange-rate"
              title={
                showTimer && !estimate?.pegged && !isLoadingReviewPayment
                  ? t('wallets.send.exchangeRateUpdate', { seconds: timeLeft })
                  : t('wallets.send.exchangeRate')
              }
              body={
                estimate && showTimer
                  ? `1 ${estimate?.walletCurrency} = ${exchangeRate} ${estimate?.paidCurrency}`
                  : '-'
              }
              size="large"
            />
          )}
          <DataListItem
            id="processing-fee"
            title={t('wallets.send.processingFee')}
            body={estimate ? `${estimate?.feePredictedAmount} ${estimate?.feeCurrency}` : '-'}
            size="large"
          />
          <DataListItem
            id="network-fee"
            title={t('wallets.send.networkFee')}
            body={
              estimate
                ? `${estimate?.networkFeePredictedAmount} ${estimate?.networkFeeCurrency}`
                : '-'
            }
            size="large"
          />
          <DataListItem
            id="total-subtracted"
            title={t('wallets.send.totalSubtracted')}
            body={estimate ? `${estimate?.totalWalletAmount} ${estimate?.walletCurrency}` : '-'}
            size="large"
          />
        </Box>
      </Box>
      <Box width="full">
        <Input
          testid="reference"
          {...formFields.reference}
          disabled={isLoadingReviewPayment}
          onChange={e => {
            setValue('reference', e?.target?.value)
            setChangedField('reference')
            setSource('user')
          }}
        />
      </Box>

      <Box flex justify="between">
        <Button testId="back" variant="secondary" onClick={walletAction?.onBack}>
          {t('back')}
        </Button>

        <Button
          testId="review-payment"
          type="submit"
          variant="primary"
          loading={isLoadingReviewPayment}
          disabled={isDisabledReviewPayment}
        >
          {t('wallets.send.reviewPayment')}
        </Button>
      </Box>
    </Form>
  )
}
